import { LAYOUTS, type Slot, type StudioLayout } from "./layouts";

/**
 * The four shipped arrangements, drawn rather than named.
 *
 * ## Why a picture beats the label
 *
 * "Split", "Artwork with camera" and "Camera" were four text buttons in a
 * two-by-two grid with a sentence underneath explaining whichever one was
 * selected. That is a paragraph of reading and a hundred and fifty pixels of
 * column to answer a question - where does my face go - that a thumbnail
 * answers instantly and without the sentence.
 *
 * ## The diagram is generated from the preset
 *
 * Each block is positioned from the same normalised slots the compositor paints
 * from, so a thumbnail cannot drift from what it stands for. Hand-drawn icons
 * would look better and would start lying the first time somebody nudged a slot
 * by two percent.
 */
export function LayoutPicker({
  selected,
  onPick,
}: {
  /** The preset the scene still matches, or null once it has been edited. */
  selected: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-4 gap-1.5" data-studio-layouts>
      {LAYOUTS.map((layout) => (
        <button
          key={layout.id}
          type="button"
          onClick={() => onPick(layout.id)}
          aria-pressed={selected === layout.id}
          aria-label={layout.label}
          title={`${layout.label} — ${layout.hint}`}
          data-studio-layout={layout.id}
          className="group flex flex-col gap-1"
        >
          <span
            className={`block overflow-hidden rounded-[5px] border transition-colors ${
              selected === layout.id
                ? "border-accent"
                : "border-line group-hover:border-line-lit"
            }`}
          >
            <Diagram layout={layout} active={selected === layout.id} />
          </span>

          <span
            className={`truncate text-center text-[10px] transition-colors ${
              selected === layout.id
                ? "text-ink"
                : "text-faint group-hover:text-soft"
            }`}
          >
            {layout.label}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * One arrangement, at thumbnail size.
 *
 * Sixteen by nine, matching the broadcast, so the proportions a host judges
 * here are the proportions they get. The camera is the solid block because it
 * is the subject; the card and the badge are outlines, because at this size a
 * third filled rectangle is a muddle rather than a layout.
 */
function Diagram({ layout, active }: { layout: StudioLayout; active: boolean }) {
  const place = (slot: Slot) => ({
    left: `${slot.x * 100}%`,
    top: `${slot.y * 100}%`,
    width: `${slot.w * 100}%`,
    height: `${slot.h * 100}%`,
  });

  return (
    <span className="relative block aspect-video w-full bg-deep">
      {layout.camera && (
        <span
          className={`absolute transition-colors ${
            active ? "bg-accent/70" : "bg-soft/30"
          }`}
          style={{
            ...place(layout.camera),
            borderRadius: layout.cameraShape === "circle" ? "50%" : "1px",
          }}
        />
      )}

      {layout.card && (
        <span
          className={`absolute rounded-[1px] border transition-colors ${
            active ? "border-accent/80" : "border-soft/40"
          }`}
          style={place(layout.card)}
        />
      )}

      {layout.brand && (
        <span
          className={`absolute rounded-[1px] transition-colors ${
            active ? "bg-accent/40" : "bg-soft/20"
          }`}
          style={place(layout.brand)}
        />
      )}
    </span>
  );
}
